import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../PublicLayout/PublicLayout';
import './AboutPage.css';

function AboutPage() {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      <div className="about-page">
        <div className="about-page__container">

          {/* Hero */}
          <section className="about-page__hero">
            <h1 className="about-page__hero-title">A return to memory.</h1>
            <p className="about-page__hero-subtitle">
              Remori is a private space to write freely, reflect honestly, and
              understand your emotions over time — without distraction, without
              an audience.
            </p>
            <button
              className="about-page__hero-cta"
              onClick={() => navigate('/')}
            >
              Start writing
            </button>
          </section>

          <hr className="about-page__divider" />

          {/* What it is */}
          <section className="about-page__section">
            <h2 className="about-page__section-title">What is Remori?</h2>
            <p className="about-page__body">
              Remori is a personal journaling app built around one idea: your
              thoughts deserve a quiet, permanent home. No timelines, no
              notifications from other people, no algorithmic pressure. Just a
              clean editor and the words you choose to put in it.
            </p>
            <p className="about-page__body">
              The name comes from the Latin <em>memori</em> — memory. We built
              Remori because we believe the act of writing things down is one of
              the most powerful habits a person can build. It sharpens thinking,
              surfaces patterns, and creates an honest record of who you are at
              any given moment.
            </p>
          </section>

          {/* How it works */}
          <section className="about-page__section">
            <h2 className="about-page__section-title">How it works</h2>
            <div className="about-page__features">
              <div className="about-page__feature">
                <div className="about-page__feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="about-page__feature-title">Write daily</h3>
                  <p className="about-page__feature-desc">
                    Open the app, write whatever is on your mind. There is no
                    wrong format — a sentence, a stream of consciousness, a
                    list. Remori saves it all.
                  </p>
                </div>
              </div>

              <div className="about-page__feature">
                <div className="about-page__feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
                <div>
                  <h3 className="about-page__feature-title">Gain insights</h3>
                  <p className="about-page__feature-desc">
                    Remori surfaces patterns in your writing over time — mood
                    trends, recurring themes, and emotional rhythms — so you can
                    understand yourself more clearly.
                  </p>
                </div>
              </div>

              <div className="about-page__feature">
                <div className="about-page__feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div>
                  <h3 className="about-page__feature-title">Private by default</h3>
                  <p className="about-page__feature-desc">
                    Your entries are encrypted in transit and at rest. We do not
                    read your journal, sell your data, or share it with third
                    parties. Your words belong to you.
                  </p>
                </div>
              </div>

              <div className="about-page__feature">
                <div className="about-page__feature-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                    <line x1="12" y1="18" x2="12.01" y2="18"/>
                  </svg>
                </div>
                <div>
                  <h3 className="about-page__feature-title">Everywhere you are</h3>
                  <p className="about-page__feature-desc">
                    Available on the web now, with native iOS and Android apps
                    coming soon. Your journal syncs seamlessly across all your
                    devices so you can write wherever a thought finds you.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Philosophy */}
          <section className="about-page__section about-page__section--callout">
            <blockquote className="about-page__quote">
              "The journal is a vehicle for my sense of selfhood. It represents
              me as emotionally and spiritually independent."
            </blockquote>
            <cite className="about-page__cite">— Susan Sontag</cite>
          </section>

          {/* Contact */}
          <section className="about-page__section">
            <h2 className="about-page__section-title">Get in touch</h2>
            <p className="about-page__body">
              We are a small team that cares deeply about building something
              genuinely useful. If you have questions, feedback, or just want
              to say hello, reach out at{' '}
              <a
                href="mailto:info@devhopeslabs.com"
                className="about-page__link"
              >
                info@devhopeslabs.com
              </a>
              .
            </p>
            <p className="about-page__body">
              To raise a complaint or report an issue, please use our{' '}
              <a
                href="https://devhopeslabs.com/contact"
                className="about-page__link"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact form
              </a>
              {' '}and our team will get back to you as soon as possible.
            </p>
          </section>

        </div>
      </div>
    </PublicLayout>
  );
}

export default AboutPage;
